import {
    Box,
    TextField,
    Switch,
    FormControlLabel,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Divider,
    CircularProgress,
    Alert,
    AlertTitle,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Warning as WarningIcon,
    Add as AddIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { FreightRecord, updateFreightRecord } from '../api/handleFreightRecords';
import { getAllCompanies, getCompaniesByType, Company, createCompany } from '../api/handleCompanies';
import { useAccount } from '../contexts/AccountContext';
import { useAlert } from '../contexts/AlertContext';
import EnhancedCompanyDropdown from './EnhancedCompanyDropdown';

interface UnmatchedCompany {
    extractedName: string;
    role: 'carrier' | 'supplier' | 'outlet';
    field: string;
    suggestions?: Array<{ id: string; name: string; types: string[] }>;
}

interface FreightFormProps {
    initialData: FreightRecord | null;
    unmatchedCompanies?: UnmatchedCompany[];
}

const FreightForm: React.FC<FreightFormProps> = ({ initialData, unmatchedCompanies = [] }) => {
    const { currentAccount, isImpersonating, impersonatedUser } = useAccount();
    const { showAlert } = useAlert();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [carrierCompanies, setCarrierCompanies] = useState<Company[]>([]);
    const [supplierCompanies, setSupplierCompanies] = useState<Company[]>([]);
    const [outletCompanies, setOutletCompanies] = useState<Company[]>([]);
    const [billToCompanies, setBillToCompanies] = useState<Company[]>([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);

    // Unmatched company resolution state
    const [resolvedCompanies, setResolvedCompanies] = useState<{ [key: string]: string }>({});
    const [createCompanyDialogOpen, setCreateCompanyDialogOpen] = useState(false);
    const [companyToCreate, setCompanyToCreate] = useState<UnmatchedCompany | null>(null);
    const [creatingCompany, setCreatingCompany] = useState(false);

    useEffect(() => {
        fetchCompanies();
    }, [currentAccount, isImpersonating, impersonatedUser]);

    const fetchCompanies = async () => {
        setCompaniesLoading(true);
        try {
            // Prepare impersonation data for API calls
            const impersonationData = isImpersonating && impersonatedUser && currentAccount ? {
                isImpersonating: true,
                impersonatedUserId: impersonatedUser.id,
                accountId: currentAccount.account_id,
            } : undefined;

            // Fetch all companies and categorized companies in parallel
            const [
                allCompanies,
                carriers,
                suppliers,
                outlets,
                billToComps
            ] = await Promise.all([
                getAllCompanies(impersonationData),
                getCompaniesByType('carrier', impersonationData),
                getCompaniesByType('supplier', impersonationData),
                getCompaniesByType('outlet', impersonationData),
                getCompaniesByType('billto', impersonationData)
            ]);

            setCompanies(allCompanies);
            setCarrierCompanies(carriers);
            setSupplierCompanies(suppliers);
            setOutletCompanies(outlets);
            setBillToCompanies(billToComps);
        } catch (error) {
            console.error('Error fetching companies:', error);
        } finally {
            setCompaniesLoading(false);
        }
    };

    const addNewCompanyToState = (newCompany: Company) => {
        // Add to all companies list
        setCompanies(prev => [...prev, newCompany]);

        // Add to appropriate role-specific lists
        if (newCompany.isCarrier) {
            setCarrierCompanies(prev => [...prev, newCompany]);
        }
        if (newCompany.isSupplier) {
            setSupplierCompanies(prev => [...prev, newCompany]);
        }
        if (newCompany.isOutlet) {
            setOutletCompanies(prev => [...prev, newCompany]);
        }
        if (newCompany.isBillTo) {
            setBillToCompanies(prev => [...prev, newCompany]);
        }
    };

    const updateCompanyInState = (updatedCompany: Company) => {
        // Update in all companies list
        setCompanies(prev => prev.map(c => c.id === updatedCompany.id ? updatedCompany : c));

        // Update role-specific lists
        if (updatedCompany.isCarrier) {
            setCarrierCompanies(prev => {
                const exists = prev.some(c => c.id === updatedCompany.id);
                return exists ? prev.map(c => c.id === updatedCompany.id ? updatedCompany : c) : [...prev, updatedCompany];
            });
        }
        if (updatedCompany.isSupplier) {
            setSupplierCompanies(prev => {
                const exists = prev.some(c => c.id === updatedCompany.id);
                return exists ? prev.map(c => c.id === updatedCompany.id ? updatedCompany : c) : [...prev, updatedCompany];
            });
        }
        if (updatedCompany.isOutlet) {
            setOutletCompanies(prev => {
                const exists = prev.some(c => c.id === updatedCompany.id);
                return exists ? prev.map(c => c.id === updatedCompany.id ? updatedCompany : c) : [...prev, updatedCompany];
            });
        }
        if (updatedCompany.isBillTo) {
            setBillToCompanies(prev => {
                const exists = prev.some(c => c.id === updatedCompany.id);
                return exists ? prev.map(c => c.id === updatedCompany.id ? updatedCompany : c) : [...prev, updatedCompany];
            });
        }
    };

    const getCompanyName = (companyId: string | null | undefined): string => {
        if (!companyId) return '';
        const company = companies.find(c => c.id === companyId);
        return company?.name || '';
    };

    const getValidCompanyId = (companyId: string | null | undefined, filterFn?: (c: Company) => boolean): string => {
        if (!companyId) return '';

        const companyExists = filterFn
            ? companies.filter(filterFn).some(c => c.id === companyId)
            : companies.some(c => c.id === companyId);

        return companyExists ? companyId : '';
    };

    const handleResolveCompany = (unmatchedCompany: UnmatchedCompany, selectedCompanyId: string) => {
        setResolvedCompanies(prev => ({
            ...prev,
            [unmatchedCompany.field]: selectedCompanyId
        }));

        // Update the form field
        formik.setFieldValue(unmatchedCompany.field, selectedCompanyId);

        // Update legacy field too
        const selectedCompany = companies.find(c => c.id === selectedCompanyId);
        if (selectedCompany) {
            const legacyField = unmatchedCompany.role === 'carrier' ? 'carrier' :
                unmatchedCompany.role === 'supplier' ? 'supplier' : 'outlet';
            formik.setFieldValue(legacyField, selectedCompany.name);
        }
    };

    const handleCreateNewCompany = async (unmatchedCompany: UnmatchedCompany) => {
        setCreatingCompany(true);
        try {
            if (!currentAccount) {
                throw new Error('No account selected');
            }

            const newCompanyData = {
                name: unmatchedCompany.extractedName,
                isCarrier: unmatchedCompany.role === 'carrier',
                isSupplier: unmatchedCompany.role === 'supplier',
                isOutlet: unmatchedCompany.role === 'outlet',
                isBillTo: false,
                isActive: true,
                address: '',
                city: '',
                state: '',
                zipCode: '',
                phone: '',
                email: '',
                contactPerson: '',
                notes: `Auto-created from document parsing: ${unmatchedCompany.extractedName}`,
            };

            const impersonationData = isImpersonating && impersonatedUser && currentAccount ? {
                isImpersonating: true,
                impersonatedUserId: impersonatedUser.id,
                accountId: currentAccount.account_id,
            } : undefined;

            const newCompany = await createCompany(newCompanyData, currentAccount.account_id, impersonationData);

            // Refresh companies list
            await fetchCompanies();

            // Auto-select the new company
            handleResolveCompany(unmatchedCompany, newCompany.id!);

            setCreateCompanyDialogOpen(false);
            setCompanyToCreate(null);
        } catch (error) {
            console.error('Error creating company:', error);
            showAlert('Failed to create company. Please try again.', 'error');
        } finally {
            setCreatingCompany(false);
        }
    };

    const formik = useFormik({
        initialValues: {
            // Legacy string fields (still used for display/fallback)
            outlet: initialData?.outlet || '',
            carrier: initialData?.carrier || '',
            supplier: initialData?.supplier || '',

            // New company ID fields
            invoicerCompanyId: initialData?.invoicerCompanyId || '',
            carrierCompanyId: resolvedCompanies.carrierCompanyId || initialData?.carrierCompanyId || '',
            shipperCompanyId: resolvedCompanies.shipperCompanyId || initialData?.shipperCompanyId || '',
            consigneeCompanyId: resolvedCompanies.consigneeCompanyId || initialData?.consigneeCompanyId || '',
            billToCompanyId: resolvedCompanies.billToCompanyId || initialData?.billToCompanyId || '',

            // Other freight data
            deliveryDate: initialData?.deliveryDate || null,
            deliveryMonth: initialData?.deliveryMonth || '',
            ticketNumber: initialData?.ticketNumber || '',
            bolNumber: initialData?.bolNumber || '',
            netTons: initialData?.netTons || null,
            grossPrice: initialData?.grossPrice || null,
            freightCost: initialData?.freightCost || null,
            netGrossPrice: initialData?.netGrossPrice || null,
            driver: initialData?.driver || '',
            dustCost: initialData?.dustCost || null,
            paidStatus: initialData?.paidStatus || false,
            confidenceScore: initialData?.confidenceScore || 0,
            needsReview: initialData?.needsReview || false,
            vendorType: initialData?.vendorType || '',
        },
        validationSchema: Yup.object({
            deliveryDate: Yup.date().nullable().required('Delivery date is required'),
            ticketNumber: Yup.string().required('Ticket number is required'),
            bolNumber: Yup.string().required('BOL number is required'),
            netTons: Yup.number().min(0, 'Must be positive').required('Net tons is required'),
            grossPrice: Yup.number().min(0, 'Must be positive').required('Gross price is required'),
            freightCost: Yup.number().min(0, 'Must be positive').required('Freight cost is required'),
            driver: Yup.string().required('Driver is required'),
            vendorType: Yup.string().required('Vendor type is required'),
        }),
        onSubmit: async (values) => {
            try {
                if (!initialData?.id) {
                    showAlert('Missing the id to update the record!', 'error');
                    return;
                }

                // Update legacy string fields based on selected companies
                const updatedValues = {
                    ...values,
                    // Keep legacy fields in sync with company selections
                    carrier: getCompanyName(values.carrierCompanyId) || values.carrier,
                    supplier: getCompanyName(values.shipperCompanyId) || values.supplier,
                    outlet: getCompanyName(values.consigneeCompanyId) || values.outlet,
                };

                // Get impersonation data for update
                const impersonationData = isImpersonating && impersonatedUser && currentAccount ? {
                    isImpersonating: true,
                    impersonatedUserId: impersonatedUser.id,
                    accountId: currentAccount.account_id,
                } : undefined;

                const results = await updateFreightRecord(initialData.id, updatedValues, impersonationData);
                console.log('Updated record:', results);
                showAlert('Record updated successfully!', 'success');
            } catch (error) {
                console.error('Update error:', error);
                showAlert(error instanceof Error ? error.message : 'Failed to update record', 'error');
            }
        },
    });

    // Update form values when resolvedCompanies changes
    useEffect(() => {
        Object.entries(resolvedCompanies).forEach(([field, companyId]) => {
            formik.setFieldValue(field, companyId);
        });
    }, [resolvedCompanies]);

    if (companiesLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading companies...</Typography>
            </Box>
        );
    }

    const unresolvedCompanies = unmatchedCompanies.filter(uc => !resolvedCompanies[uc.field]);

    return (
        <Box component="form" onSubmit={formik.handleSubmit} p={2} noValidate>
            {/* Unmatched Companies Alert */}
            {unresolvedCompanies.length > 0 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <AlertTitle>
                        <Box display="flex" alignItems="center" gap={1}>
                            <WarningIcon />
                            Companies Need Attention ({unresolvedCompanies.length})
                        </Box>
                    </AlertTitle>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Some companies extracted from the document don't match existing companies in your database.
                    </Typography>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">
                                Resolve Unmatched Companies
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {unresolvedCompanies.map((unmatchedCompany, index) => (
                                <Card key={index} sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                            <Box>
                                                <Typography variant="subtitle2">
                                                    "{unmatchedCompany.extractedName}"
                                                </Typography>
                                                <Chip
                                                    label={unmatchedCompany.role.toUpperCase()}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>
                                            <Button
                                                variant="outlined"
                                                startIcon={<AddIcon />}
                                                onClick={() => {
                                                    setCompanyToCreate(unmatchedCompany);
                                                    setCreateCompanyDialogOpen(true);
                                                }}
                                            >
                                                Create New Company
                                            </Button>
                                        </Box>

                                        {unmatchedCompany.suggestions && unmatchedCompany.suggestions.length > 0 && (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Or select an existing company:
                                                </Typography>
                                                <Box display="flex" gap={1} flexWrap="wrap">
                                                    {unmatchedCompany.suggestions.map((suggestion) => (
                                                        <Chip
                                                            key={suggestion.id}
                                                            label={`${suggestion.name} (${suggestion.types.join(', ')})`}
                                                            onClick={() => handleResolveCompany(unmatchedCompany, suggestion.id)}
                                                            variant="outlined"
                                                            clickable
                                                            size="small"
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                </Alert>
            )}

            {/* Resolved Companies Success */}
            {Object.keys(resolvedCompanies).length > 0 && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleIcon />
                        <Typography>
                            {Object.keys(resolvedCompanies).length} companies resolved successfully!
                        </Typography>
                    </Box>
                </Alert>
            )}

            {/* Company Information Section */}
            <Typography variant="h6" gutterBottom>
                Company Information
            </Typography>
            <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                gap={2}
                mb={3}
            >
                <EnhancedCompanyDropdown
                    label="Invoicing Company"
                    value={getValidCompanyId(formik.values.invoicerCompanyId)}
                    onChange={(companyId) => {
                        formik.setFieldValue('invoicerCompanyId', companyId);
                    }}
                    companies={companies}
                    allCompanies={companies}
                    requiredRole="any"
                    onCompaniesRefresh={fetchCompanies}
                    onNewCompany={addNewCompanyToState}
                    onCompanyUpdate={updateCompanyInState}
                />

                <EnhancedCompanyDropdown
                    label="Carrier"
                    value={getValidCompanyId(formik.values.carrierCompanyId, c => c.isCarrier)}
                    onChange={(companyId) => {
                        formik.setFieldValue('carrierCompanyId', companyId);
                        // Auto-update legacy field
                        const selectedCompany = companies.find(c => c.id === companyId);
                        if (selectedCompany) {
                            formik.setFieldValue('carrier', selectedCompany.name);
                        }
                    }}
                    companies={carrierCompanies}
                    allCompanies={companies}
                    requiredRole="carrier"
                    onCompaniesRefresh={fetchCompanies}
                    onNewCompany={addNewCompanyToState}
                    onCompanyUpdate={updateCompanyInState}
                />

                <EnhancedCompanyDropdown
                    label="Supplier"
                    value={getValidCompanyId(formik.values.shipperCompanyId, c => c.isSupplier)}
                    onChange={(companyId) => {
                        formik.setFieldValue('shipperCompanyId', companyId);
                        // Auto-update legacy field
                        const selectedCompany = companies.find(c => c.id === companyId);
                        if (selectedCompany) {
                            formik.setFieldValue('supplier', selectedCompany.name);
                        }
                    }}
                    companies={supplierCompanies}
                    allCompanies={companies}
                    requiredRole="supplier"
                    onCompaniesRefresh={fetchCompanies}
                    onNewCompany={addNewCompanyToState}
                    onCompanyUpdate={updateCompanyInState}
                />

                <EnhancedCompanyDropdown
                    label="Outlet"
                    value={getValidCompanyId(formik.values.consigneeCompanyId, c => c.isOutlet)}
                    onChange={(companyId) => {
                        formik.setFieldValue('consigneeCompanyId', companyId);
                        // Auto-update legacy field
                        const selectedCompany = companies.find(c => c.id === companyId);
                        if (selectedCompany) {
                            formik.setFieldValue('outlet', selectedCompany.name);
                        }
                    }}
                    companies={outletCompanies}
                    allCompanies={companies}
                    requiredRole="outlet"
                    onCompaniesRefresh={fetchCompanies}
                    onNewCompany={addNewCompanyToState}
                    onCompanyUpdate={updateCompanyInState}
                />

                <EnhancedCompanyDropdown
                    label="Bill To Company"
                    value={getValidCompanyId(formik.values.billToCompanyId, c => c.isBillTo)}
                    onChange={(companyId) => {
                        formik.setFieldValue('billToCompanyId', companyId);
                    }}
                    companies={billToCompanies}
                    allCompanies={companies}
                    requiredRole="billto"
                    onCompaniesRefresh={fetchCompanies}
                    onNewCompany={addNewCompanyToState}
                    onCompanyUpdate={updateCompanyInState}
                />
            </Box>


            <Divider sx={{ my: 3 }} />

            {/* Freight Details Section */}
            <Typography variant="h6" gutterBottom>
                Freight Details
            </Typography>
            <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                gap={2}
            >
                <DatePicker
                    label="Del Date"
                    value={formik.values.deliveryDate ? dayjs(formik.values.deliveryDate) : null}
                    onChange={(date) => formik.setFieldValue('deliveryDate', date)}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            error: formik.touched.deliveryDate && Boolean(formik.errors.deliveryDate),
                            helperText: formik.touched.deliveryDate && formik.errors.deliveryDate,
                        },
                    }}
                />
                <TextField
                    label="Del Month"
                    name="deliveryMonth"
                    fullWidth
                    value={formik.values.deliveryMonth}
                    onChange={formik.handleChange}
                />
                <TextField
                    label="Ticket #"
                    name="ticketNumber"
                    fullWidth
                    value={formik.values.ticketNumber}
                    onChange={formik.handleChange}
                    error={formik.touched.ticketNumber && Boolean(formik.errors.ticketNumber)}
                    helperText={formik.touched.ticketNumber && formik.errors.ticketNumber}
                />
                <TextField
                    label="BOL #"
                    name="bolNumber"
                    fullWidth
                    value={formik.values.bolNumber}
                    onChange={formik.handleChange}
                    error={formik.touched.bolNumber && Boolean(formik.errors.bolNumber)}
                    helperText={formik.touched.bolNumber && formik.errors.bolNumber}
                />
                <TextField
                    label="Net Tons"
                    name="netTons"
                    type="number"
                    fullWidth
                    value={formik.values.netTons ?? ''}
                    onChange={formik.handleChange}
                    error={formik.touched.netTons && Boolean(formik.errors.netTons)}
                    helperText={formik.touched.netTons && formik.errors.netTons}
                />
                <TextField
                    label="GP"
                    name="grossPrice"
                    type="number"
                    fullWidth
                    value={formik.values.grossPrice ?? ''}
                    onChange={formik.handleChange}
                    error={formik.touched.grossPrice && Boolean(formik.errors.grossPrice)}
                    helperText={formik.touched.grossPrice && formik.errors.grossPrice}
                />
                <TextField
                    label="FRT Cost"
                    name="freightCost"
                    type="number"
                    fullWidth
                    value={formik.values.freightCost ?? ''}
                    onChange={formik.handleChange}
                    error={formik.touched.freightCost && Boolean(formik.errors.freightCost)}
                    helperText={formik.touched.freightCost && formik.errors.freightCost}
                />
                <TextField
                    label="Net GP"
                    name="netGrossPrice"
                    type="number"
                    fullWidth
                    value={formik.values.netGrossPrice ?? ''}
                    onChange={formik.handleChange}
                />
                <TextField
                    label="Driver"
                    name="driver"
                    fullWidth
                    value={formik.values.driver}
                    onChange={formik.handleChange}
                    error={formik.touched.driver && Boolean(formik.errors.driver)}
                    helperText={formik.touched.driver && formik.errors.driver}
                />
                <TextField
                    label="Dust Cost"
                    name="dustCost"
                    type="number"
                    fullWidth
                    value={formik.values.dustCost ?? ''}
                    onChange={formik.handleChange}
                />
                <TextField
                    label="Vendor Type"
                    name="vendorType"
                    fullWidth
                    value={formik.values.vendorType}
                    onChange={formik.handleChange}
                    error={formik.touched.vendorType && Boolean(formik.errors.vendorType)}
                    helperText={formik.touched.vendorType && formik.errors.vendorType}
                />
                <TextField
                    label="Confidence Score"
                    name="confidenceScore"
                    type="number"
                    fullWidth
                    value={formik.values.confidenceScore}
                    onChange={formik.handleChange}
                    inputProps={{ min: 0, max: 1, step: 0.01 }}
                />
            </Box>

            {/* Status Section */}
            <Box mt={3} mb={3}>
                <Typography variant="h6" gutterBottom>
                    Status
                </Typography>
                <Box display="flex" gap={3}>
                    <FormControlLabel
                        control={
                            <Switch
                                name="paidStatus"
                                checked={formik.values.paidStatus}
                                onChange={formik.handleChange}
                            />
                        }
                        label="Paid"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                name="needsReview"
                                checked={formik.values.needsReview}
                                onChange={formik.handleChange}
                            />
                        }
                        label="Needs Review"
                    />
                </Box>
            </Box>

            <Box mt={3}>
                <Button type="submit" variant="contained" size="large">
                    Save Changes
                </Button>
            </Box>

            {/* Create Company Dialog */}
            <Dialog
                open={createCompanyDialogOpen}
                onClose={() => setCreateCompanyDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Create New Company
                </DialogTitle>
                <DialogContent>
                    {companyToCreate && (
                        <Box>
                            <Typography variant="body1" gutterBottom>
                                Create a new company for:
                            </Typography>
                            <Typography variant="h6" color="primary" gutterBottom>
                                "{companyToCreate.extractedName}"
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                This will be created as a <strong>{companyToCreate.role}</strong> company.
                                You can edit additional details later in the Companies section.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setCreateCompanyDialogOpen(false)}
                        disabled={creatingCompany}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => companyToCreate && handleCreateNewCompany(companyToCreate)}
                        variant="contained"
                        disabled={creatingCompany}
                        startIcon={creatingCompany ? <CircularProgress size={16} /> : <AddIcon />}
                    >
                        {creatingCompany ? 'Creating...' : 'Create Company'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FreightForm;
